import { stripIndexFileName } from '$lib/services/contents/collection/nested';

/**
 * @import { Entry, FlattenedEntryContent, InternalCollection } from '$lib/types/private';
 * @import { RelationFieldFilterOptions } from '$lib/types/public';
 */

const FIELD_TEMPLATE_REGEX = /^{{fields\.(.+?)}}$/;

/**
 * Resolve `{{fields.fieldName}}` and `{{slug}}` template strings in filter values against the entry
 * currently being edited. Unresolvable templates are dropped from the values array so they do not
 * accidentally match `undefined` content fields.
 * @param {RelationFieldFilterOptions[]} filters Entry filters.
 * @param {FlattenedEntryContent | undefined} currentLocaleValues Current locale field values of the
 * entry being edited, or `undefined` when not available (e.g. backlink resolution).
 * @param {string | undefined} currentSlug Current slug of the entry being edited, or `undefined`
 * when not yet determined (e.g. new entry draft).
 * @returns {RelationFieldFilterOptions[]} Filters with template values resolved.
 */
export const resolveFilterValues = (filters, currentLocaleValues, currentSlug = undefined) =>
  filters.map(({ field, values, exclude }) => ({
    field,
    exclude,
    values: values.flatMap((v) => {
      if (typeof v !== 'string') return [v];

      if (v === '{{slug}}') {
        // Drop if slug not yet determined (new entry draft)
        return currentSlug ? [currentSlug] : [];
      }

      const match = v.match(FIELD_TEMPLATE_REGEX);

      if (!match) return [v];

      // Template found — resolve against current entry values
      const resolved = currentLocaleValues?.[match[1]];

      // Drop unresolvable templates to avoid false matches
      return resolved !== undefined ? [resolved] : [];
    }),
  }));

/**
 * Filter entries based on file name and entry filters.
 * @param {object} args Arguments.
 * @param {Entry[]} args.refEntries Reference entries.
 * @param {InternalCollection} args.collection Collection the entries belong to.
 * @param {string} args.locale Current locale.
 * @param {string} [args.fileName] File name to filter by.
 * @param {RelationFieldFilterOptions[]} [args.entryFilters] Entry filters to apply.
 * @param {string} [args.defaultLocale] Default locale from collection’s i18n configuration.
 * @returns {{ refEntry: Entry, content: FlattenedEntryContent }[]} Filtered entries with content.
 */
export const filterAndPrepareEntries = ({
  refEntries,
  collection,
  locale,
  fileName = undefined,
  entryFilters = [],
  defaultLocale = undefined,
}) =>
  refEntries
    .filter((refEntry) => !fileName || fileName === refEntry.slug)
    .map((refEntry) => {
      // Fall back to the collection’s default locale, then `_default` (no i18n) as a final fallback
      const { content } =
        refEntry.locales[locale] ??
        (defaultLocale ? refEntry.locales[defaultLocale] : undefined) ??
        refEntry.locales._default ??
        {};

      return {
        refEntry,
        hasContent: !!content && Object.keys(content).length > 0,
        content: content ?? {},
      };
    })
    .filter(
      ({ hasContent, content, refEntry }) =>
        hasContent &&
        entryFilters.every(({ field, values, exclude = false }) => {
          // An empty values array means no constraint — skip this filter
          if (values.length === 0) return true;

          // `slug` refers to the entry’s slug, not a regular content field.
          // `fields.fieldName` strips the prefix so a field literally named `slug` can be
          // targeted via `fields.slug` without ambiguity.
          const isEntrySlug = field === 'slug';
          const fieldKey = field.replace(/^fields\./, '');

          // Match the slug in the same shape a reference to the entry uses
          const fieldValue = isEntrySlug
            ? stripIndexFileName(collection, refEntry.slug)
            : content[fieldKey];

          return exclude ? !values.includes(fieldValue) : values.includes(fieldValue);
        }),
    );
